import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Heart, Share2, Minus, Plus, ShieldCheck, Truck, Star } from 'lucide-react';
import { toast } from 'sonner';
import { api, resolveImg, money, sizeOptions } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { useChat, DEFAULT_CHAT } from '@/context/ChatContext';
import { useAuth } from '@/context/AuthContext';
import { ShareModal } from '@/components/ShareModal';
import { Countdown } from '@/components/Countdown';
import { StarRating } from '@/components/StarRating';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const RING_CHART = [['US 5', '15.7 mm'], ['US 6', '16.5 mm'], ['US 7', '17.3 mm'], ['US 8', '18.2 mm'], ['US 9', '19.0 mm'], ['US 10', '19.8 mm']];
const CUFF_CHART = [['S', 'Wrist 14–15 cm'], ['M', 'Wrist 15–16.5 cm'], ['L', 'Wrist 16.5–18 cm']];

function SizeGuideDialog({ open, onOpenChange, category }) {
  const rows = category === 'Cuffs' ? CUFF_CHART : RING_CHART;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="size-guide-dialog" className="bg-surface border-border max-w-md">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{category === 'Cuffs' ? 'Cuff' : 'Ring'} Size Guide</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground mb-2">
          {category === 'Cuffs' ? 'Measure around your wrist with a soft tape and match below.' : 'Wrap a strip of paper around your finger, mark it, and measure the length (inner circumference).'}
        </p>
        <table className="w-full text-sm">
          <tbody>
            {rows.map(([a, b]) => (
              <tr key={a} className="border-b border-border">
                <td className="py-2 font-mono text-gold">{a}</td>
                <td className="py-2 text-muted-foreground text-right">{b}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-muted-foreground mt-3">Between sizes? We recommend sizing up. Contact our concierge for bespoke sizing.</p>
      </DialogContent>
    </Dialog>
  );
}

function NotifyBox({ productId, defaultEmail }) {
  const [email, setEmail] = useState(defaultEmail || '');
  const [done, setDone] = useState(false);
  const submit = async () => {
    if (!email) { toast.error('Enter your email'); return; }
    try { await api.post(`/products/${productId}/notify`, { email }); setDone(true); toast.success("We'll email you when it's back"); }
    catch { toast.error('Could not sign you up'); }
  };
  if (done) return <div data-testid="notify-done" className="border border-border bg-surface p-4 text-sm text-muted-foreground mb-6">You're on the list — we'll email you the moment it returns.</div>;
  return (
    <div className="border border-border bg-surface p-4 mb-6">
      <p className="text-sm mb-3">Sold out — get notified when it's back in stock.</p>
      <div className="flex gap-2">
        <Input data-testid="notify-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="bg-background border-border" />
        <button data-testid="notify-submit-button" onClick={submit} className="bg-gold text-primary-foreground px-5 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover whitespace-nowrap">Notify Me</button>
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const { setMessage } = useChat();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [size, setSize] = useState('');
  const [showGuide, setShowGuide] = useState(false);

  const loadReviews = () => api.get(`/products/${id}/reviews`).then(({ data }) => setReviews(data));

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data); setLiked(data.liked_by_me); setLikes(data.likes || 0);
    }).catch(() => setNotFound(true));
    loadReviews();
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!product) return;
    const url = window.location.href;
    document.title = `${product.name} — Ke-Sa Jewellery`;
    const desc = `${product.name}: ${product.description} Handcrafted gold-plated brass by Ke-Sa.`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'description'); document.head.appendChild(m); }
    m.setAttribute('content', desc);

    const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;
    const availability = product.is_presale ? 'https://schema.org/PreOrder' : (product.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock');
    const ld = {
      '@context': 'https://schema.org', '@type': 'Product', name: product.name, description: product.description,
      image: (product.images || []).map(resolveImg), category: product.category,
      brand: { '@type': 'Brand', name: 'Ke-Sa' },
      offers: { '@type': 'Offer', price: product.price, priceCurrency: (product.currency || 'usd').toUpperCase(), availability, url },
    };
    if (avg > 0) ld.aggregateRating = { '@type': 'AggregateRating', ratingValue: avg.toFixed(1), reviewCount: reviews.length };
    let s = document.getElementById('product-jsonld');
    if (!s) { s = document.createElement('script'); s.id = 'product-jsonld'; s.type = 'application/ld+json'; document.head.appendChild(s); }
    s.textContent = JSON.stringify(ld);

    setMessage(`Hi Ke-Sa, I'm interested in the ${product.name} — ${url}`);
    return () => {
      document.title = 'Ke-Sa — Handcrafted Gold-Plated Jewellery';
      const el = document.getElementById('product-jsonld');
      if (el) el.remove();
      setMessage(DEFAULT_CHAT);
    };
  }, [product, reviews]);

  if (notFound) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-center px-4">
      <p className="font-serif text-3xl">Piece not found</p>
      <p className="text-muted-foreground text-sm">This item may have sold out or moved.</p>
      <a href="/shop" className="bg-gold text-primary-foreground px-6 py-3 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover">Browse the Collection</a>
    </div>
  );
  if (!product) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Loading…</div>;

  const soldOut = !product.is_presale && product.stock <= 0;
  const avgRating = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;
  const sizes = sizeOptions(product.category);

  const addToBag = () => {
    if (sizes && !size) { toast.error('Please select a size'); return; }
    add(product, qty, size || null);
  };

  const toggleLike = async () => {
    if (!user) { toast.error('Sign in to save favourites'); return; }
    const { data } = await api.post(`/products/${product.id}/like`);
    setLiked(data.liked); setLikes(data.likes);
  };

  const submitReview = async () => {
    if (!user) { toast.error('Sign in to leave a review'); return; }
    await api.post(`/products/${product.id}/reviews`, { rating, comment });
    setComment(''); setRating(5); loadReviews();
    toast.success('Thank you for your review');
  };

  return (
    <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-12 lg:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        {/* Gallery */}
        <div>
          <div className="aspect-[4/5] bg-surface overflow-hidden mb-4">
            <img src={resolveImg(product.images?.[activeImg])} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-3">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-24 overflow-hidden border ${i === activeImg ? 'border-gold' : 'border-border'}`}>
                  <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {(product.video_url || product.youtube_id) && (
            <div className="mt-4 aspect-video bg-surface overflow-hidden">
              {product.youtube_id ? (
                <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${product.youtube_id}`} title="video" allowFullScreen />
              ) : (
                <video className="w-full h-full object-cover" src={resolveImg(product.video_url)} controls muted loop />
              )}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <p className="eyebrow mb-3">{product.category}</p>
          <h1 className="font-serif text-4xl lg:text-5xl mb-4">{product.name}</h1>
          <div className="flex items-center gap-4 mb-6">
            <StarRating value={avgRating} size={16} />
            <span className="text-xs text-muted-foreground">{reviews.length} verified reviews</span>
          </div>
          <p className="font-mono text-2xl text-gold mb-6">{money(product.price, product.currency)}</p>

          {product.is_presale && (
            <div data-testid="detail-presale-banner" className="border border-[#7A571B] bg-[#3B2A10]/40 rounded p-5 mb-6">
              <p className="text-[#F3D082] font-mono text-xs uppercase tracking-wider mb-2">Presale — Under Production</p>
              <p className="text-sm text-muted-foreground mb-4">
                Expected dispatch: <span className="text-foreground">{product.presale_dispatch_date || 'TBA'}</span>
                {product.presale_limit ? ` · Limited to ${product.presale_limit} pieces` : ''}
              </p>
              {product.presale_dispatch_date && <Countdown target={product.presale_dispatch_date} testId="presale-countdown" />}
            </div>
          )}

          <p className="text-muted-foreground leading-relaxed mb-8">{product.description}</p>

          <div className="flex items-center gap-4 mb-6">
            {sizes && (
              <div className="w-32">
                <Select value={size} onValueChange={setSize}>
                  <SelectTrigger data-testid="size-select" className="border-border h-12"><SelectValue placeholder={product.category === 'Cuffs' ? 'Size' : 'Ring size'} /></SelectTrigger>
                  <SelectContent className="bg-surface-elevated border-border">
                    {sizes.map((s) => <SelectItem key={s} value={s} data-testid={`size-option-${s}`}>{product.category === 'Cuffs' ? s : `US ${s}`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center border border-border">
              <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-12 flex items-center justify-center hover:text-gold"><Minus size={14} /></button>
              <span className="w-10 text-center font-mono">{qty}</span>
              <button data-testid="qty-inc" onClick={() => setQty((q) => q + 1)} className="w-11 h-12 flex items-center justify-center hover:text-gold"><Plus size={14} /></button>
            </div>
            <button data-testid="add-to-cart-button" disabled={soldOut} onClick={addToBag}
              className="flex-1 bg-gold text-primary-foreground py-4 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover transition-colors disabled:opacity-40">
              {soldOut ? 'Sold Out' : product.is_presale ? 'Reserve Presale' : 'Add to Bag'}
            </button>
          </div>

          {sizes && (
            <button data-testid="size-guide-button" onClick={() => setShowGuide(true)} className="text-xs text-gold underline underline-offset-4 mb-6 -mt-2 block">
              View size guide
            </button>
          )}
          {soldOut && <NotifyBox productId={product.id} defaultEmail={user?.email} />}
          <SizeGuideDialog open={showGuide} onOpenChange={setShowGuide} category={product.category} />

          <div className="flex items-center gap-6 mb-8">
            <button data-testid="detail-like-button" onClick={toggleLike} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-gold">
              <Heart size={16} className={liked ? 'fill-gold text-gold' : ''} /> {likes} Favourites
            </button>
            <button data-testid="detail-share-button" onClick={() => setShareOpen(true)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-gold">
              <Share2 size={15} /> Share
            </button>
          </div>

          <div className="space-y-3 border-t border-border pt-6">
            <div className="flex items-center gap-3 text-sm text-muted-foreground"><Truck size={16} className="text-gold" /> Delivery scheduled to your date & courier window at checkout</div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground"><ShieldCheck size={16} className="text-gold" /> Gold-plated brass · Handmade in our atelier</div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <div className="mt-24 max-w-3xl">
        <h2 className="font-serif text-3xl mb-8">Owner Reviews</h2>
        {user && (
          <div className="border border-border p-6 mb-8 bg-surface">
            <p className="text-sm mb-3">Share your experience</p>
            <StarRating value={rating} size={20} onChange={setRating} />
            <Textarea data-testid="review-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Tell us about your piece…" className="mt-4 bg-background border-border" />
            <button data-testid="submit-review-button" onClick={submitReview} className="mt-4 bg-gold text-primary-foreground px-6 py-3 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover">Submit Review</button>
          </div>
        )}
        <div className="space-y-6">
          {reviews.length === 0 && <p className="text-muted-foreground text-sm">No reviews yet. Be the first.</p>}
          {reviews.map((r) => (
            <div key={r.id} className="border-b border-border pb-6">
              <div className="flex items-center justify-between mb-2">
                <p className="font-serif text-lg">{r.user_name}</p>
                <StarRating value={r.rating} />
              </div>
              <p className="text-sm text-muted-foreground">{r.comment}</p>
            </div>
          ))}
        </div>
      </div>

      <ShareModal open={shareOpen} onOpenChange={setShareOpen} product={product} />
    </div>
  );
}
